import { createApp } from 'vue'
import { createPinia } from 'pinia'
import CarbonVue from '@carbon/vue'
import { registerSW } from 'virtual:pwa-register'
import App from './App.vue'
import router, { markAuthInitialized } from './router'
import i18n, { loadRemoteMessages } from './plugins/i18n'
import { useAuthStore } from './stores/auth.store'
import { useInstallStore } from './stores/install.store'
import { loadConfig } from './config'
import { initClient } from './api/client'

// Carbon Design System styles
import 'carbon-components/scss/globals/scss/styles.scss'

// Global styles
import './styles/main.scss'

// Fiscozen Partner Design System (loaded AFTER Carbon so its tokens and
// utility classes win the cascade for the new PMS / Portal shells).
import './design-system/tokens.css'
import './design-system/type.css'
import './design-system/shell.css'
import './design-system/errors.css'

// ── PWA service-worker registration ──────────────────────────────
//
// `registerType: 'prompt'` (vite.config.ts) keeps a new SW waiting until
// every tab of the site is closed, and nothing here activates it earlier:
// activation hands every open tab to the new worker (controllerchange),
// which drops the old build's precache under tabs still running it. The
// waiting worker costs nothing — navigations are served from the network,
// so a tab opened after a release runs the new build whichever worker is
// active; `useVersionCheck` moves the tabs left open on the old build.
//
// Skipped in dev (`devOptions.enabled = false`); the import resolves
// to a no-op shim served by vite-plugin-pwa.
registerSW({
  onOfflineReady() {
    // No toast on first cache — the partner doesn't need to know
    // the SW just primed itself. The first offline navigation
    // surfaces the OfflineView when relevant.
  },
})

// ── PWA install-prompt capture ───────────────────────────────────
//
// Chrome / Edge fire `beforeinstallprompt` once per page load when
// install criteria are met. We stash the event in `installStore` so
// the surface that wants to render the prompt (currently
// `PortalShell` only — partners install on phones; PMS users sit
// at desks) can call `prompt()` from a user gesture.
//
// Pinia is created inside `loadConfig().then(...)` below; install
// the listener at the same point so `useInstallStore()` finds an
// active instance.

// Suppress SessionExpiredError from async contexts (store actions, etc.)
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && typeof event.reason === 'object' && 'isSessionExpired' in event.reason) {
    event.preventDefault()
  }
})

// Load runtime config first, then bootstrap the app
loadConfig().then(async () => {
  initClient()

  const app = createApp(App)
  const pinia = createPinia()

  app.use(pinia)
  app.use(router)
  app.use(CarbonVue)
  app.use(i18n)

  // Install-prompt capture must wait until Pinia is registered.
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    useInstallStore().captureEvent(event as BeforeInstallPromptEvent)
  })

  // Suppress SessionExpiredError — the overlay handles UX
  app.config.errorHandler = (err) => {
    if (err && typeof err === 'object' && 'isSessionExpired' in err) return
    throw err // Re-throw non-session errors so Vue's default handler catches them
  }

  // Load translations, init auth, then mount
  await loadRemoteMessages().catch(() => {})
  const authStore = useAuthStore()
  await authStore.init().catch(() => {})
  markAuthInitialized()
  app.mount('#app')
})
